import React, { useEffect, useMemo, useState } from 'react';

// Wiederverwendbare Illustration: N Batteriespeicher in einem Raster, von einem
// zentralen Trafo aus vermascht. Rein visuelles hyper-mesh-Konzept, KEINE echten
// Live-Daten. Auf Hover zeigt jeder Speicher einen (deterministisch erzeugten)
// Performance-Wert an. Gedacht als Baustein für mehrere Seiten (Investor-Ansicht
// heute, Monitoring-Ansichten später) — daher generisch gehalten.

function seededRandom(seed) {
  let t = seed + 0x6d2b79f5;
  return function () {
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function darken(hex, amount) {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, ((n >> 16) & 255) * (1 - amount));
  const g = Math.max(0, ((n >> 8) & 255) * (1 - amount));
  const b = Math.max(0, (n & 255) * (1 - amount));
  return `rgb(${r.toFixed(0)},${g.toFixed(0)},${b.toFixed(0)})`;
}

function colorForValue(value) {
  if (value < 93) return '#f87171';
  if (value < 98.5) return '#38bdf8';
  if (value < 99.3) return '#2dd4bf';
  return '#4ade80';
}

function buildUnits(rows, cols, seed, colW, rowH, gridLeft, gridTop) {
  const units = [];
  let n = 1;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const rand = seededRandom(seed * 1000 + n);
      const isServiceCase = rand() < 0.04;
      const value = isServiceCase ? 78 + rand() * 14 : 97 + rand() * 2.9;
      const x = gridLeft + c * colW + r * 11;
      const y = gridTop + r * rowH - c * 3;
      units.push({ n, x, y, value: Math.round(value * 10) / 10, color: colorForValue(value) });
      n += 1;
    }
  }
  return units;
}

export default function StorageMeshGrid({
  rows = 10,
  cols = 10,
  seed = 1,
  trafoLabel = 'Trafo',
  callouts = {},
  mode = 'investor',
  caption = null,
  trafoLoad = null,          // 0..1 (optional): färbt den Trafo grün/gelb/rot
  onTrafoClick = null,       // optional: macht den Trafo klickbar
  trafoAriaLabel = null,
  onFaultCount = null,       // optional: meldet die Zahl der roten Servicefälle
}) {
  const colW = 92;
  const rowH = 66;
  const gridLeft = 150;
  const gridTop = 230;
  const width = 1200;
  const height = 1000;
  const transformerX = width / 2;
  const transformerY = 90;

  const units = useMemo(
    () => buildUnits(rows, cols, seed, colW, rowH, gridLeft, gridTop),
    [rows, cols, seed]
  );

  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (onFaultCount) onFaultCount(units.filter((u) => u.value < 93).length);
  }, [units, onFaultCount]);

  const trafoColor = trafoLoad == null ? '#4ade80' : trafoLoad > 0.8 ? '#f87171' : trafoLoad > 0.62 ? '#fbbf24' : '#4ade80';
  const trafoAlert = trafoLoad != null && trafoLoad > 0.8;

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" role="img">
        <title>hyper-mesh Speicher-Raster</title>
        <desc>
          Schematische Darstellung von {units.length} Batteriespeicher-Containern, versorgt von
          einem zentralen Trafo über ein feines Leitungsnetz. Jeder Container ist einzeln
          angesteuert; Farbe und Hover-Wert zeigen eine illustrative Performance-Kennzahl.
        </desc>

        <g stroke="#8fd9c4" strokeWidth="0.6" opacity="0.18">
          {units.map((u) => (
            <line
              key={`line-${u.n}`}
              x1={transformerX}
              y1={transformerY + 40}
              x2={u.x}
              y2={u.y}
            />
          ))}
        </g>

        <g
          transform={`translate(${transformerX - 40},${transformerY - 40})`}
          style={onTrafoClick ? { cursor: 'pointer' } : undefined}
          onClick={onTrafoClick || undefined}
          onKeyDown={onTrafoClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTrafoClick(); } } : undefined}
          role={onTrafoClick ? 'button' : undefined}
          tabIndex={onTrafoClick ? 0 : undefined}
          aria-label={onTrafoClick ? trafoAriaLabel || trafoLabel : undefined}
          data-testid="storage-mesh-trafo"
        >
          {trafoAlert && (
            <circle cx="40" cy="40" r="52" fill={trafoColor} opacity="0.25">
              <animate attributeName="r" values="46;66;46" dur="1.4s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.35;0.05;0.35" dur="1.4s" repeatCount="indefinite" />
            </circle>
          )}
          <rect width="80" height="80" rx="10" fill="#0f2027" stroke={trafoColor} strokeWidth="2" />
          <path d="M46 10 L28 46 L42 46 L34 70 L56 34 L42 34 Z" fill={trafoColor} />
        </g>
        <text
          x={transformerX}
          y={transformerY + 58}
          textAnchor="middle"
          fill="#bfe6fa"
          fontSize="14"
          fontFamily="sans-serif"
          fontWeight="600"
        >
          {trafoLabel}
        </text>
        {trafoLoad != null && (
          <text
            x={transformerX}
            y={transformerY + 76}
            textAnchor="middle"
            fill={trafoColor}
            fontSize="14"
            fontFamily="sans-serif"
            fontWeight="700"
          >
            {Math.round(trafoLoad * 100)} %
          </text>
        )}

        {units.map((u) => {
          const isHovered = hovered === u.n;
          const ext = 16;
          const A = [u.x - 13, u.y - 6];
          const B = [u.x + 9, u.y - 10];
          const C = [u.x + 13, u.y + 4];
          const D = [u.x - 9, u.y + 8];
          const A2 = [A[0], A[1] + ext];
          const B2 = [B[0], B[1] + ext];
          const C2 = [C[0], C[1] + ext];
          const D2 = [D[0], D[1] + ext];
          const pts = (arr) => arr.map((p) => p.join(',')).join(' ');
          const scale = isHovered ? 1.35 : 1;

          return (
            <g
              key={u.n}
              style={{
                cursor: 'pointer',
                transformOrigin: `${u.x}px ${u.y}px`,
                transform: `scale(${scale})`,
                transition: 'transform 120ms ease-out',
              }}
              onMouseEnter={() => setHovered(u.n)}
              onMouseLeave={() => setHovered((h) => (h === u.n ? null : h))}
            >
              <circle
                cx={u.x}
                cy={u.y}
                r={isHovered ? 26 : 18}
                fill={u.color}
                opacity={isHovered ? 0.55 : 0.3}
                style={{ filter: 'blur(6px)', transition: 'all 120ms ease-out' }}
              />
              {/* front-bottom face (darkest) */}
              <polygon points={pts([C, D, D2, C2])} fill={darken(u.color, 0.55)} stroke="#0b1210" strokeWidth="1" />
              {/* right face (darker) */}
              <polygon points={pts([B, C, C2, B2])} fill={darken(u.color, 0.3)} stroke="#0b1210" strokeWidth="1" />
              {/* top face (brightest) */}
              <polygon points={pts([A, B, C, D])} fill={u.color} stroke="#0b1210" strokeWidth="1" />
            </g>
          );
        })}

        {Object.entries(callouts).map(([nStr, label]) => {
          const n = Number(nStr);
          const u = units.find((unit) => unit.n === n);
          if (!u) return null;
          const [dx, dy] = label.offset || [100, -40];
          const lx = u.x + dx;
          const ly = u.y + dy;
          const anchor = dx < 0 ? 'end' : 'start';
          const tx = lx + (dx < 0 ? -8 : 8);
          return (
            <g key={`callout-${n}`}>
              <line x1={u.x} y1={u.y} x2={lx} y2={ly} stroke="#eef2f0" strokeWidth="1" opacity="0.6" />
              <circle cx={lx} cy={ly} r="2.5" fill="#eef2f0" />
              <text x={tx} y={ly + 4} textAnchor={anchor} fill="#eef2f0" fontSize="15" fontFamily="sans-serif" fontWeight="600">
                {label.text || label}
              </text>
            </g>
          );
        })}

        {hovered !== null && (() => {
          const u = units.find((unit) => unit.n === hovered);
          if (!u) return null;
          const boxW = 108;
          const boxH = mode === 'investor' ? 34 : 48;
          const bx = Math.min(Math.max(u.x - boxW / 2, 10), width - boxW - 10);
          const by = Math.max(u.y - 46 - boxH, 10);
          return (
            <g>
              <rect x={bx} y={by} width={boxW} height={boxH} rx="8" fill="#0f172a" stroke={u.color} strokeWidth="1.5" opacity="0.95" />
              <text x={bx + boxW / 2} y={by + 20} textAnchor="middle" fill="#f8fafc" fontSize="15" fontFamily="sans-serif" fontWeight="700">
                {u.value}%
              </text>
              {mode !== 'investor' && (
                <text x={bx + boxW / 2} y={by + 34} textAnchor="middle" fill="#94a3b8" fontSize="10" fontFamily="sans-serif">
                  Speicher {u.n}
                </text>
              )}
            </g>
          );
        })()}
      </svg>
      {caption && <p className="text-sm text-slate-400 mt-2">{caption}</p>}
    </div>
  );
}
